import { useTranslation } from 'react-i18next';
import { Button, FilterToggleButton, PageHeader } from '../../../ui/index.js';
import { hasPermission, type PermissionsBundle } from '../../../stores/auth.js';
import { canCreateRequest, roleSubtitleKey } from '../role.js';
import type { RoleKind } from '../types.js';
import type { RequestsBatchExportController } from '../selection/useRequestSelection.js';

interface RequestsListPageHeaderProps {
  role: RoleKind;
  permissions: PermissionsBundle;
  narrow: boolean;
  advancedOpen: boolean;
  advancedActiveCount: number;
  onToggleAdvanced: () => void;
  chipsOpen: boolean;
  onToggleChips: () => void;
  activeChipCount: number;
  chipsGroupId: string;
  onCreateSimple: () => void;
  onCreate: () => void;
  onCreateContainer: () => void;
  batchExport?: Pick<
    RequestsBatchExportController,
    | 'selectionMode'
    | 'selectedCount'
    | 'selectingAll'
    | 'enter'
    | 'exit'
    | 'clear'
    | 'selectAll'
    | 'openExport'
  >;
}

/**
 * @aiPurpose Header row for `RequestsListPage` — title + subtitle on
 *   the left, filter-toggle + create buttons on the right (collapsed
 *   into a single column on narrow viewports). Pure presentation;
 *   create gating lives in the parent so the page owns the "user is
 *   present" branch.
 */
export function RequestsListPageHeader({
  role,
  permissions,
  narrow,
  advancedOpen,
  advancedActiveCount,
  onToggleAdvanced,
  chipsOpen,
  onToggleChips,
  activeChipCount,
  chipsGroupId,
  onCreateSimple,
  onCreate,
  onCreateContainer,
  batchExport,
}: RequestsListPageHeaderProps) {
  const { t } = useTranslation(['requests', 'common']);
  const canCreate = canCreateRequest(permissions);
  const canBulkExport = hasPermission(permissions, 'requests.export.bulk');
  const selectionMode = batchExport?.selectionMode ?? false;
  const selectedCount = batchExport?.selectedCount ?? 0;
  return (
    <PageHeader
      title={t('requests:list.title')}
      subtitle={t(roleSubtitleKey(role))}
      narrow={narrow}
      actions={
        <>
          <FilterToggleButton
            open={chipsOpen}
            activeCount={activeChipCount}
            icon="layout-list"
            label={t('requests:list.statusesToggle')}
            size="md"
            aria-controls={chipsGroupId}
            onClick={onToggleChips}
          />
          <FilterToggleButton
            open={advancedOpen}
            activeCount={advancedActiveCount}
            label={t('requests:list.filters')}
            size="md"
            aria-controls="requests-filter-panel"
            onClick={onToggleAdvanced}
          />
          {canBulkExport && !selectionMode && (
            <Button kind="secondary" onClick={() => batchExport?.enter()}>
              {t('requests:export.bulk.enterSelection')}
            </Button>
          )}
          {canBulkExport && selectionMode && (
            <>
              <Button
                kind="secondary"
                disabled={batchExport?.selectingAll === true}
                onClick={() => void batchExport?.selectAll()}
              >
                {batchExport?.selectingAll
                  ? t('requests:export.bulk.selectingAll')
                  : t('requests:export.bulk.selectAll')}
              </Button>
              <Button
                kind="ghost"
                disabled={selectedCount === 0}
                onClick={() => batchExport?.clear()}
              >
                {t('requests:export.bulk.clear')}
              </Button>
              <Button kind="ghost" onClick={() => batchExport?.exit()}>
                {t('requests:export.bulk.exitSelection')}
              </Button>
              <Button
                kind="primary"
                disabled={selectedCount === 0}
                onClick={() => batchExport?.openExport()}
              >
                {t('requests:export.bulk.open', { count: selectedCount })}
              </Button>
            </>
          )}
          {canCreate && !selectionMode && (
            <Button kind="primary" icon="plus" onClick={onCreateSimple}>
              {t('requests:list.newSimple')}
            </Button>
          )}
          {canCreate && !selectionMode && (
            <Button kind="secondary" icon="plus" onClick={onCreate}>
              {t('requests:list.newRequest')}
            </Button>
          )}
          {canCreate && !selectionMode && (
            <Button kind="secondary" icon="plus" onClick={onCreateContainer}>
              {t('requests:list.newContainer')}
            </Button>
          )}
        </>
      }
    />
  );
}
